import React, { useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Typography,
} from '@material-ui/core';
import CustomTooltip from '../BuildingBlocks/Tooltip/Tooltip';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { IProduct } from '../../Types/product';

const useStyles = makeStyles((theme) => ({
  products: {
    display: 'flex',
    justifyContent: 'space-between',
    width: 450,
    height: 180,
    marginBottom: theme.spacing(1),
  },
  card: { width: 200, maxHeight: 200 },
  cardsContainer: {
    display: 'flex',
    overflowX: 'hidden',
  },
  asin: {
    fontWeight: 'bold',
    fontSize: '1em',
    direction: 'rtl',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  name: { fontWeight: 'bold', fontSize: '1em' },
  img: { maxHeight: 60, maxWidth: 60, marginBottom: theme.spacing(1) },
  selected: { border: '3px solid ' + theme.palette.primary.main },
  avatar: {
    height: 60,
    width: 60,
    borderRadius: '100%',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
  },
  nextBackButton: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiSvgIcon-root': {
      fontSize: '2.5rem',
    },
  },
  backButton: { marginRight: theme.spacing(1) },
  nextButton: { marginLeft: theme.spacing(1) },
}));

export default function ProductCarousel({
  products,
  handleOnSelect,
  selectedProduct,
}: {
  products: Array<IProduct>;
  selectedProduct?: IProduct;
  handleOnSelect: (id: string) => void;
}) {
  const classes = useStyles();

  function calcPosition() {
    let selectedProductIndex = products.findIndex(
      (item) => item.id === selectedProduct?.id,
    );
    selectedProductIndex === -1 && (selectedProductIndex = 0);
    return selectedProductIndex % 2 === 0
      ? selectedProductIndex
      : selectedProductIndex - 1;
  }

  const currentPosition = calcPosition();

  const [firstProduct, setFirstProduct] = useState(currentPosition);
  const [lastProduct, setLastProduct] = useState(currentPosition + 2);

  function onSelect(id: string) {
    handleOnSelect(id);
  }

  function nextProducts() {
    setFirstProduct(firstProduct + 2);
    setLastProduct(lastProduct + 2);
  }

  function previousProducts() {
    setFirstProduct(firstProduct - 2);
    setLastProduct(lastProduct - 2);
  }

  return (
    <div className={classes.products}>
      <div className={classes.nextBackButton}>
        <IconButton
          color="primary"
          className={classes.backButton}
          onClick={previousProducts}
          disabled={firstProduct > 0 ? false : true}
        >
          <ChevronLeftIcon />
        </IconButton>
      </div>
      {products && (
        <div className={classes.cardsContainer}>
          {products?.slice(firstProduct, lastProduct)?.map((product, i) => {
            return (
              <Card
                key={i}
                className={
                  classes.card +
                  ' ' +
                  //@ts-ignore
                  (product.id === selectedProduct?.id ? classes.selected : '')
                }
              >
                <CardActionArea
                  onClick={() => {
                    onSelect(product.id);
                  }}
                >
                  <CardContent>
                    <Typography paragraph className={classes.asin}>
                      {product.asin || product.id}
                    </Typography>
                    {product.image ? (
                      <img
                        src={product.image}
                        alt="product logo"
                        className={classes.img}
                      />
                    ) : (
                      <div className={classes.avatarContainer}>
                        <Avatar
                          alt={product.name + ' image'}
                          className={classes.avatar}
                        >
                          <Typography>
                            {product.name?.slice(0, 1)?.toUpperCase()}
                          </Typography>
                        </Avatar>
                      </div>
                    )}
                    <Typography className={classes.name}>
                      {product.name?.length > 20 ? (
                        <CustomTooltip
                          arrow
                          title={<Typography>{product.name}</Typography>}
                          placement="top"
                        >
                          <div>{product.name.slice(0, 20) + '...'}</div>
                        </CustomTooltip>
                      ) : (
                        product.name
                      )}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </div>
      )}
      <div className={classes.nextBackButton}>
        <IconButton
          color="primary"
          className={classes.nextButton}
          onClick={nextProducts}
          disabled={lastProduct >= products?.length ? true : false}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>
    </div>
  );
}
